/**
 * @author Adam Vlasek <adam.vlasek@styrax.cz>
 */

import { useState, useCallback, useEffect } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import qs from 'qs';

import { useToken } from './TokenStorage';
import useMounted from './useMounted';

import { createUrl } from '../services/CommonFunctions';
import RouteEnum from '../enums/RouteEnum';
import ErrorType from '../types/ErrorType';

export interface IUseApiState<T> {
    called: boolean
    loading: boolean
    sending: boolean
    data?: T
    error?: ErrorType
    code?: number
}

export type CallType<T> = Promise<T | false>;

export interface IUseApi<T, U> extends IUseApiState<T> {
    call: (config?: AxiosRequestConfig<U>) => CallType<T>
}

export interface IUseApiProps<T, U> extends AxiosRequestConfig<U> {
    autoFetch?: boolean
    withToken?: boolean
    mock?: any
    errorFormatter?: (response: T) => string
    model?: (data: T) => T
}

// const cache = RestCache.getInstance();

/**
 * Umoznuje komunikaci s REST API. Je postaveno na knihovne axios. Automaticky zpracovava autorizacni token a v pripade 401 odhlasi aplikaci.
 * @param options Nastaveni requestu. Nastaveni je stejne jako umoznuje axios + obohaceno o:
 * withToken - prida do hlavidku auhorization s tokenem,
 * autofetch - zavola se ihned po namounteni komponenty,
 * mock - objekt, ktery si chcete vratit jako mock
 * errorFormatter - formatovaci funkce pro custom errory
 * @example const {called, loading, data, error} = useApi<ResponseType, BodyType>({method, url, data, ...});
 * @returns Vraci objekt obsahujici:
 * called - jestli uz byl request zavolan,
 * loading - jestli se request nacita (uplne ta sama hodnota jako v sending)
 * sending - jestli se request nacita (uplne ta sama hodnota jako v loading)
 * data - nactena data
 * error - chyba ze serveru
 * call - funkce na manualni zavolani requestu [call(options: IUseApiProps): void]
 */
const useApi = <T = any, U = any>({ autoFetch = false, mock, errorFormatter, ...hookConfig }: IUseApiProps<T, U>): IUseApi<T, U> => {
    // const { STAZENI_SOUBORU_SE_NEZDARILO, SPOJENI_SE_SERVEREM_JE_NEDOSTUPNE, JEJDA_NECO_SE_NEPOVEDLO, DETAIL_CHYBY } = useLanguage();
    // const { store: { errors } } = useStore();
    const mounted = useMounted();
    const isMounted = () => mounted.current;

    const [state, setState] = useState<IUseApiState<any>>({
        called: false,
        loading: false,
        sending: false,
        data: undefined,
        error: undefined,
        code: undefined
    });
    const { token, setToken } = useToken();

    const call = useCallback(
        async (config: AxiosRequestConfig<U> = {}): Promise<T | false> => {
            isMounted() && setState({ called: true, loading: true, sending: true });

            try {
                //konfigurace requestu se sklada z casti v konfiguraci, z options hooku a z options volani metody call
                const fullConfig = {
                    ...window.config.api,
                    ...hookConfig,
                    ...config
                };

                const callApi = async () => {
                    const conf = {
                        ...fullConfig,
                        paramsSerializer: (params: any) => qs.stringify(params, { arrayFormat: 'repeat' })
                    };


                    //pridani tokenu do hlavicky pozadavku
                    if (conf.withToken) {
                        conf.headers = { ...(conf.headers || {}), authorization: `Bearer ${token}` };
                    }

                    //pridani mockateeid
                    if (window.config.mock?.token) conf.headers.mockateeid = window.config.mock.token;

                    //odeslani requestu na API
                    //pokud se ma pouzit mock, tak se vrati a nic dalsiho se nedela
                    const res = await axios(conf);

                    //ulozeni noveho tokenu z odpovedi
                    if (res.headers?.authorization && isMounted()) setToken(res.headers.authorization);
                    return res;
                };

                //volba mezi rezimem mock a provolavanim API
                const res = mock ?
                    { data: mock, status: 200 } :
                    await callApi();

                const data = fullConfig.model ? fullConfig.model(res.data) : res.data;

                isMounted() && setState({ called: true, data, code: res.status, loading: false, sending: false });
                return data;
            }
            catch (e: any) {
                //odhlaseni, pokud se vrati 401
                if (e?.response?.status === 401) {
                    window.location.href = createUrl(RouteEnum.LOGOUT());
                }
                //zpracovani vracene chyby
                else if (e.response?.data) {
                    //formatovani chybove hlasky
                    if (isMounted()) {
                        let message = 'Jejda, něco se nepovedlo';

                        let errorId = '';

                        if (e.response?.data?.error_id) {
                            errorId = `<br /><details><summary><b>Detail chyby</b></summary>id: ${e.response.data.error_id}</details>`;
                        }

                        if (e.response.data instanceof Blob) message = 'Stažení souboru se nezdařilo';
                        else if (errorFormatter) {
                            message = errorFormatter(e.response);
                        }
                        else if (e.response.data.message) {
                            message = `${e.response.data.message}${errorId}`;
                        }

                        setState({
                            called: true,
                            error: { message, code: e.response.status },
                            loading: false,
                            sending: false,
                            code: e?.response?.status
                        });
                    }
                }
                else if (isMounted()) {
                    setState({
                        called: true,
                        error: { message: 'Spojení se serverem je nedostupné' },
                        loading: false,
                        sending: false,
                        code: 404
                    });
                }
                return false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hookConfig]
    );

    useEffect(
        () => {
            //zavolani requestu po namounteni komponenty
            if (autoFetch) call();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hookConfig.url, hookConfig.data, hookConfig.params]
    );

    return {
        ...state,
        call
    };
};

export default useApi;
